import { Link } from "react-router-dom";
import type { PurchaseOrderListRow } from "@/api/purchase";
import { Panel } from "@/components/Panel";
import { formatUsd } from "@/lib/purchase-format";
import { fmtEUR } from "@/lib/pricing-format";
import { berechneKapital } from "./kapital";
import { schraffur } from "./kapital-darstellung";

const quellenMarke = { rechnung: "Rechnung", proforma: "Proforma", ek: "ca. EK" };

export function KapitalUebersicht({ rows, darfPreise }: { rows: PurchaseOrderListRow[]; darfPreise: boolean }) {
  if (!darfPreise) return null;
  // Summen beruhen auf der gelieferten Bestellliste: LIMIT 500, neueste zuerst.
  const { bestellungen, summen } = berechneKapital(rows);
  if (bestellungen.length === 0) return null;
  const anteil = (wert: number) => `${summen.gebunden_eur > 0 ? wert / summen.gebunden_eur * 100 : 0}%`;

  return (
    <Panel title="Kapital" subtitle="Offene Bestellungen: Warenwert, bezahlt, offen – ohne Nebenkosten"
      className="min-w-0 max-w-full overflow-hidden" bodyClassName="min-w-0 p-5">
      <dl className="grid grid-cols-1 gap-3 text-ink sm:grid-cols-3 sm:gap-5">
        <div>
          <dt className="text-xs text-ink-2">Bezahlt</dt>
          <dd className="mt-1 text-lg tabular-nums">{summen.bezahlt_eur_umgerechnet && <span className="text-xs">≈ </span>}{fmtEUR(summen.bezahlt_eur)}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink-2">Noch offen</dt>
          <dd className="mt-1 text-lg tabular-nums">≈ {fmtEUR(summen.offen_eur)}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink-2">Gebunden gesamt</dt>
          <dd className="mt-1 text-xl font-semibold tabular-nums">≈ {fmtEUR(summen.gebunden_eur)}</dd>
        </div>
      </dl>
      {summen.unvollstaendig && <p className="mt-2 text-xs text-warn">EUR-Summen unvollständig – nur bekannte Beträge enthalten.</p>}

      <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-panel-2" aria-hidden="true">
        <div className="bg-success" style={{ width: anteil(summen.bezahlt_eur) }} />
        <div className="bg-success/25" style={{ width: anteil(summen.offen_mit_preis_eur) }} />
        <div className="bg-panel-2" style={{ ...schraffur, width: anteil(summen.offen_geschaetzt_eur) }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-2">
        <span className="inline-flex items-center gap-1.5"><i className="size-2 rounded-sm bg-success" aria-hidden="true" />Bezahlt</span>
        <span className="inline-flex items-center gap-1.5"><i className="size-2 rounded-sm bg-success/25" aria-hidden="true" />Offen mit Preis</span>
        <span className="inline-flex items-center gap-1.5"><i className="size-2 rounded-sm bg-panel-2" style={schraffur} aria-hidden="true" />Offen geschätzt</span>
      </div>

      <div className="mt-4 min-w-0 max-w-full overflow-x-auto" role="region" aria-label="Kapital je Bestellung" tabIndex={0}>
        <table className="w-full min-w-[600px] text-sm text-ink tabular-nums" aria-label="Kapitalübersicht">
          <thead>
            <tr className="border-b border-line text-xs text-ink-2">
              <th scope="col" className="py-2 pr-4 text-left font-medium">Bestellung</th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">Bestellwert</th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">Bezahlt</th>
              <th scope="col" className="py-2 text-right font-medium">Offen</th>
            </tr>
          </thead>
          <tbody>
            {bestellungen.map((row) => (
              <tr key={row.id} className="border-b border-line/50 align-top">
                <th scope="row" className="py-3 pr-4 text-left font-normal">
                  <Link to={`/einkauf/${row.id}`} className="font-medium underline-offset-4 hover:underline focus-visible:underline">{row.order_number}</Link>
                  <div className="mt-0.5 text-xs text-ink-2">{row.supplier_name ?? "—"}</div>
                </th>
                <td className="py-3 pr-4 text-right">
                  <div className={row.geschaetzt ? "italic" : undefined}>
                    {row.geschaetzt ? `ca. ${fmtEUR(row.wert_eur)}` : formatUsd(row.wert_usd)}
                  </div>
                  {row.quelle && <span className="mt-0.5 inline-block rounded border border-line px-1.5 text-[11px] text-ink-2">{quellenMarke[row.quelle]}</span>}
                  {row.quelle === "proforma" && row.positions_ohne_preis > 0 && <div className="mt-1 text-xs text-warn">{row.positions_ohne_preis} Pos. ohne Preis</div>}
                  {(row.quelle === "ek" || row.quelle === null) && row.positions_ohne_ek > 0 && <div className="mt-1 text-xs text-warn">{row.positions_ohne_ek} Pos. ohne EK</div>}
                  {row.quelle === null && <div className="mt-1 text-xs text-ink-2">Kein Wert</div>}
                </td>
                <td className="py-3 pr-4 text-right">
                  <div>{formatUsd(row.bezahlt_usd)}</div>
                  <div className="mt-0.5 text-xs text-ink-2">{row.bezahlt_eur_umgerechnet && "≈ "}{fmtEUR(row.bezahlt_eur)}</div>
                </td>
                <td className="py-3 text-right">
                  {row.geschaetzt ? <div className="italic">ca. {fmtEUR(row.offen_eur)}</div> : (
                    <>
                      <div>{formatUsd(row.offen_usd)}</div>
                      {row.offen_eur !== null && <div className="mt-0.5 text-xs text-ink-2">≈ {fmtEUR(row.offen_eur)}</div>}
                      {row.offen_usd !== null && row.offen_eur === null && <div className="mt-0.5 text-xs text-warn">Kein Zahlungskurs</div>}
                    </>
                  )}
                  {row.ueberzahlt !== null && row.ueberzahlt > 0 && <div className="mt-1 text-xs text-warn">Überzahlt um {row.geschaetzt ? fmtEUR(row.ueberzahlt) : formatUsd(row.ueberzahlt)}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-ink-2">≈ umgerechnet mit dem Kurs der Zahlungen; ca. = geschätzt aus unserem EK (inkl. Fracht/Zoll, eher etwas hoch).</p>
    </Panel>
  );
}
